<template>
  <DialogWrapper
    close-on-cancel
    destroyOnClose
    class="ai-comparison-dialog"
    :hiddenHeaderLine="!aiComparisonIsLoading"
    hideFooter
    :titleI18nKey="aiComparisonIsLoading ? `${i18nPrefix}.dialogTitle.aiComparisonLoadingTitle` : ''"
    :style="{'--dialog-width': getComparisonDialogWidth()}"
    :loading="isLoading"
    :show.sync="isShow"
    @closed="handleClosed"
  >
    <div v-if="!aiComparisonIsLoading" class="ai-comparison-content">
      <div class="ai-resume-comparison-table">
        <div class="col header">
          <div class="block block-compare-categories">
            <div class="table compare-categories-table">
              <div class="block-criteria criteria">
              </div>
              <ul class="categories">
                <li
                  v-for="(category, index) in usedCriterionList"
                  class="item category-title"
                  :ref="`categoryRef_${index}`"
                  :key="index"
                >
                  <div v-auto-tooltip class="category-label">{{ category.i18nName }}</div>
                </li>
                <li v-if="!isHiddenContent" class="item category-title total-summary">
                  <div v-auto-tooltip class="category-label">{{ $t(`${i18nPrefix}.label.aiComparisonSummaryTitle`) }}</div>
                  <i v-auto-tooltip.always="$t(`${i18nPrefix}.label.aiComparisonSummaryTip`)" class="comparison-tip-icon el-icon-question"></i>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-for="(candidate, index) in candidateComparisonList" class="col candidate-info" :class="index === candidateComparisonList.length - 1 && 'is-last'" :key="index" :style="{ '--score-color': getJobFitScoreColor(candidate.total_score, noComparisonColor) }" @mouseenter="handleMouseEnter(candidate)" @mouseleave="handleMouseLeave(candidate)">
          <div class="block block-candidate" :class="[candidate.noComparison && 'no-comparison']">
            <div v-show="candidate.showTotalFit" class="ai-comparison-fit">
              <div class="fit-label">
                <span class="score">{{ candidate.total_score }}</span>
              </div>
            </div>
            <div v-if="candidate.recommended" class="ai-recommend">{{ $t(`${i18nPrefix}.label.aiComparisonRecommendedTitle`) }}</div>
            <div class="table table-info">
              <div class="block-criteria candidate-name">
                <div class="img-avatar" @click="handleOpenDetail(candidate.candidate_id)">
                  <img :src="getCandidateAvatarPath(candidate)">
                </div>
                <div class="name-location">
                  <div v-auto-tooltip class="name" @click="handleOpenDetail(candidate.candidate_id)">{{ candidate.name }}</div>
                  <div class="location">
                    <i class="iconfont iconAPP-location"></i>
                    <span v-auto-tooltip class="location-name">{{ candidate.locationI18nName|empty }}</span>
                  </div>
                </div>
              </div>
              <ul class="categories candidate-info-content" ref="candidateInfoContentRef">
                <li
                  v-for="(criterionInfo, criterionIndex) in candidate.criterionList"
                  class="item"
                  :ref="`criterionRef_${criterionIndex}`"
                  :class="[criterionInfo.score === 'A' && 'is-max']"
                  :key="criterionIndex"
                >
                  <div v-auto-tooltip.always="{ content: criterionInfo.tooltipContent, disabled: !criterionInfo.hasToolTip }" class="comparison-result">{{ criterionInfo.criterionI18nSummary }}</div>
                  <div class="result-icon" :style="{ '--criterion-score-color': getJobFitScoreColor(criterionInfo.score, noComparisonColor) }"></div>
                </li>
              </ul>
              <div class="summary-info" ref="totalSummaryRef">
                <div class="summary-body">
                  <div class="content">
                    <div v-if="candidate.recommended" class="ai-recommend">{{ $t(`${i18nPrefix}.label.aiComparisonRecommendedTitle`) }}</div>
                    <div class="summary">{{ candidate.totalI18nSummary }}</div>
                  </div>
                </div>
              </div>
              <div v-if="candidate.noComparison" class="no-comparison-tip" ref="noComparisonTipRef">{{ $t(`${i18nPrefix}.label.noComparisonTip`) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="aiComparisonIsLoading" class="ai-comparison-loading">
      <div class="start-label">{{ $t(`${i18nPrefix}.AI_COMPARISON_TASK_STATUS.START`) }}</div>
      <div class="ai-loading-container">
        <div v-rect-loading="aiComparisonIsLoading" class="icon"></div>
        <transition name="fade" mode="out-in">
          <div class="text is-pending" :key="currentLoadingTextIndex">
            <div class="label">{{ loadingTextOptions[currentLoadingTextIndex] }}</div>
          </div>
        </transition>
      </div>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { getJobFitScoreColor } from '@/utils/common'
import { addI18nProperty } from '@/i18n'
import { AI_COMPARISON_TASK_STATUS } from '@/constant/application'

export default {
  components: {
    DialogWrapper
  },
  props: {
    categories: {
      type: Array,
      required: true
    },
    applicationList: {
      type: Array,
      required: true
    },
    comparisonResultList: {
      type: Array,
      default: () => []
    },
    show: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    aiComparisonLoading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      getJobFitScoreColor,
      candidateComparisonList: [],
      currentLoadingTextIndex: -1,
      loadingIntervalId: null,
      noComparisonColor: '#fafafa'
    }
  },
  computed: {
    isShow: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    },
    isLoading: {
      get () {
        return this.loading
      },
      set (val) {
        this.$emit('update:loading', val)
      }
    },
    aiComparisonIsLoading: {
      get () {
        return this.aiComparisonLoading
      },
      set (val) {
        this.$emit('update:aiComparisonLoading', val)
      }
    },
    isHiddenContent () {
      return this.isLoading || this.aiComparisonIsLoading
    },
    usedCriterionList () {
      if (!this.comparisonResultList.length || !this.comparisonResultList[0].criterion_list.length) {
        return []
      }
      // 必须从比较结果中确定哪些是这次比较使用的准则，否则会报错
      const criterionList = this.comparisonResultList[0].criterion_list
      const criterionKeyList = _.map(criterionList, criterion => criterion.key)
      const usedCriterionList = _.filter(this.categories, category => _.includes(criterionKeyList, `${category.id}`))
      return _.sortBy(usedCriterionList, 'order')
    },
    loadingTextOptions () {
      const list = _.map(AI_COMPARISON_TASK_STATUS, (val, key) => {
        if (key === 'START') {
          return
        }
        return this.$t(`${this.i18nPrefix}.AI_COMPARISON_TASK_STATUS.${val}`)
      })
      return _.filter(list)
    }
  },
  created () {
  },
  methods: {
    initPage () {
      const maxCriterionScoreMap = {}
      const scoreMap = {
        A: 5,
        B: 4,
        C: 3,
        D: 2,
        E: 1
      }
      _.forEach(this.comparisonResultList, item => {
        _.forEach(item.criterion_list, criterion => {
          const key = criterion.key
          const score = scoreMap[criterion.score]
          if (!_.has(maxCriterionScoreMap, key) || score > maxCriterionScoreMap[key]) {
            maxCriterionScoreMap[key] = score
          }
        })
      })

      const hasResultCandidateIds = []
      const list = _.map(this.comparisonResultList, item => {
        const candidateId = item.candidate_id
        hasResultCandidateIds.push(`${candidateId}`)
        const application = _.find(this.applicationList, application => `${application.candidate.id}` === `${candidateId}`)
        const candidate = application.candidate
        const criterionList = _.chain(item.criterion_list).map(criterion => {
          const category = _.find(this.categories, category => `${category.id}` === criterion.key)
          const order = category.order
          addI18nProperty(criterion, 'criterionI18nSummary', 'summary_cn', 'summary_en')
          const maxScore = maxCriterionScoreMap[criterion.key]
          const currentScore = scoreMap[criterion.score]
          const isMax = maxScore === currentScore
          return {
            ...criterion,
            order,
            isMax,
            tooltipContent: category.i18nName,
            hasToolTip: true
          }
        }).orderBy('order').value()
        addI18nProperty(item, 'totalI18nSummary', 'total_summary_cn', 'total_summary_en')
        return {
          ...item,
          ...candidate,
          criterionList: criterionList,
          locationI18nName: application.locationI18nName,
          showTotalFit: false
        }
      })
      this.candidateComparisonList = this.formatListWithMountain(_.sortBy(list, item => scoreMap[item.total_score]))
      const noResultApplicationList = _.chain(this.applicationList).filter(application => !_.includes(hasResultCandidateIds, `${application.candidate.id}`)).orderBy('candidate.id').value()
      const criterionList = this.candidateComparisonList.length ? _.cloneDeep(this.candidateComparisonList[0].criterionList) : []
      _.forEach(criterionList, criterion => {
        criterion.criterionI18nSummary = null
        criterion.hasToolTip = true
        criterion.isMax = false
        criterion.score = null
        criterion.summary_cn = null
        criterion.summary_en = null
        criterion.tooltipContent = null
      })
      _.forEach(noResultApplicationList, application => {
        this.candidateComparisonList.push({
          ...application.candidate,
          total_score: null,
          criterionList: criterionList,
          candidate_id: application.candidate.id,
          locationI18nName: application.locationI18nName,
          noComparison: true
        })
      })
    },
    /**
     * 将数组根据顺序成 '山型' 排列
     * @param list: 升序排列后的数组
     */
    formatListWithMountain (list) {
      // 从第一个开始隔两个取出一个
      const mountainLeft = []
      let mountainRight = []
      _.forEach(list, (item, index) => {
        if (index % 2 === 0) {
          mountainLeft.push(item)
        } else {
          mountainRight = _.concat(item, mountainRight)
        }
      })
      return _.concat(mountainLeft, mountainRight)
    },
    getComparisonDialogWidth () {
      const length = this.candidateComparisonList.length
      if (!length || this.aiComparisonIsLoading) {
        return '60%'
      }
      let weight = 18
      if (length < 3) {
        weight = 30
      } else if (length <= 4) {
        weight = 22
      }
      return `${length * weight}%`
    },
    resetCategoryItemStyle () {
      _.forEach(this.usedCriterionList, (item, index) => {
        const categoryRef = this.$refs[`categoryRef_${index}`][0]
        const criterionRefList = this.$refs[`criterionRef_${index}`]
        const categoryElHeight = categoryRef.offsetHeight
        let maxHeight = categoryElHeight
        _.forEach(criterionRefList, criterionRef => {
          const criterionElHeight = criterionRef.offsetHeight
          if (criterionElHeight > maxHeight) {
            maxHeight = criterionElHeight
          }
        })
        if (categoryElHeight !== maxHeight) {
          categoryRef.style.height = `${maxHeight}px`
        }
        _.forEach(criterionRefList, criterionRef => {
          criterionRef.style.height = `${maxHeight}px`
        })

        const totalSummaryRefList = this.$refs.totalSummaryRef
        let maxSummaryHeight = 0
        _.forEach(totalSummaryRefList, summaryEl => {
          if (summaryEl.offsetHeight > maxSummaryHeight) {
            maxSummaryHeight = summaryEl.offsetHeight
          }
        })
        _.forEach(totalSummaryRefList, summaryEl => {
          summaryEl.style.height = `${maxSummaryHeight}px`
        })
      })
    },
    resetComparisonTipStyle () {
      const candidateInfoContentRefList = this.$refs.candidateInfoContentRef
      const totalSummaryRefList = this.$refs.totalSummaryRef
      const noComparisonTipRefList = this.$refs.noComparisonTipRef
      if (!candidateInfoContentRefList?.length || !totalSummaryRefList?.length || !noComparisonTipRefList?.length) {
        return
      }
      let contentHeight = 0
      _.forEach(candidateInfoContentRefList, item => {
        contentHeight = item.offsetHeight > contentHeight ? item.offsetHeight : contentHeight
      })
      let summaryHeight = 0
      _.forEach(totalSummaryRefList, item => {
        summaryHeight = item.offsetHeight > summaryHeight ? item.offsetHeight : summaryHeight
      })
      _.forEach(noComparisonTipRefList, item => {
        item.style.height = `${contentHeight + summaryHeight + 25}px`
      })
    },
    getCandidateAvatarPath (candidate) {
      return candidate.photo ? this.$DjangoBaseUrl(candidate.photo) : this.$DjangoStaticUrl('images_en/default.jpg')
    },
    handleMouseEnter (candidate) {
      if (candidate.noComparison) {
        return
      }
      this.$nextTick(() => {
        candidate.showTotalFit = true
      })
    },
    handleMouseLeave (candidate) {
      if (candidate.noComparison) {
        return
      }
      this.$nextTick(() => {
        candidate.showTotalFit = false
      })
    },
    handleOpenDetail (candidateId) {
      this.$emit('openDetail', candidateId)
    },
    showNextLine () {
      if (this.currentLoadingTextIndex < this.loadingTextOptions.length - 1) {
        this.currentLoadingTextIndex++
      } else {
        clearInterval(this.loadingIntervalId)
      }
    },
    startAIComparisonLoading () {
      this.showNextLine()
      this.loadingIntervalId = setInterval(() => {
        this.showNextLine()
      }, 5000)
    },
    resetLoadingText () {
      this.currentLoadingTextIndex = this.loadingTextOptions.length - 1
    },
    handleClosed () {
      this.isShow = false
      this.currentLoadingTextIndex = -1
      this.aiComparisonIsLoading = false
      if (this.loadingIntervalId) {
        clearInterval(this.loadingIntervalId)
      }
    }
  },
  watch: {
    isShow: {
      handler () {
        this.initPage()
        if (!this.isShow) {
          this.currentLoadingTextIndex = -1
          this.aiComparisonIsLoading = false
          if (this.loadingIntervalId) {
            clearInterval(this.loadingIntervalId)
          }
        }
      }
    },
    comparisonResultList: {
      handler () {
        this.initPage()
        this.$nextTick(() => {
          this.resetCategoryItemStyle()
          this.resetComparisonTipStyle()
        })
      }
    },
    aiComparisonIsLoading: {
      handler () {
        if (this.aiComparisonIsLoading) {
          this.startAIComparisonLoading()
        } else {
          this.resetLoadingText()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
.ai-comparison-dialog {
  .ai-comparison-content {
    background-color: #fff;
    padding: 30px 26px 20px 30px;
    overflow: auto;
    .ai-resume-comparison-table {
      display: flex;
      flex-wrap: nowrap;
      .col {
        flex: 1 1 17%;
        .block {
          display: inline-block;
          position: relative;
          width: 100%;
          margin-bottom: 30px;
          border-radius: 0px;
          color: rgba(0, 0, 0, 0.87);
          background: transparent;
          box-shadow: none;
          .table {
            width: 100%;
            max-width: 100%;
            margin-bottom: 1rem;
            background-color: transparent;
            padding: 15px;
            margin-bottom: 0px;
            .block-criteria {
              margin: 20px 0 30px 0;
              word-break: break-word;
              white-space: pre-wrap;
            }
            .categories {
              list-style: none;
              padding: 0;
              margin: 10px auto;
              border-top: 1px solid rgba(153, 153, 153, 0.3);
              .item {
                color: #3C4857;
                padding: 12px 0;
                border-bottom: 1px solid rgba(153, 153, 153, 0.3);
                word-break: break-word;
                white-space: pre-wrap;
                font-size: 14px;
              }
              .category-title {
                font-weight: 500;
                display: flex;
                align-items: center;
                .category-label {
                  font-weight: 700;
                  white-space: pre-wrap;
                  margin: auto 0 auto auto;
                }
                .comparison-tip-icon {
                  margin-left: 6px;
                }
              }
            }
          }
        }
      }
      .header {
        max-width: 14%;
        min-width: 14%;
        .block-compare-categories {
          .compare-categories-table {
            .criteria {
              text-align: right;
              height: 80px;
              display: inline-block;
              width: 100%;
              margin: 39px 0 7px 0;
              word-break: break-word;
              white-space: pre-wrap;
              .label {
                margin-top: 18px;
                font-size: 18px;
                font-weight: 700;
              }
            }
            .categories {
              .item {
                text-align: right;
                &:last-child {
                  border-bottom: 0;
                }
              }
            }
          }
        }
      }
      .candidate-info {
        .block-candidate {
          border-top: 10px solid var(--score-color);
          position: relative;
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
          .ai-comparison-fit {
            position: absolute;
            background-color: var(--score-color);
            width: 100%;
            top: -32px;
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            .fit-label {
              width: 24px;
              height: 24px;
              font-size: 14px;
              border: 2px solid #fff;
              border-radius: 100%;
              text-align: center;
              margin: 4px auto;
              color: #fff;
              position: relative;
              .score {
                position: absolute;
                top: 0px;
                left: 5px;
                width: 10px;
                height: 20px;
                line-height: 20px;
              }
            }
          }
          .ai-recommend {
            position: absolute;
            right: 0px;
            top: -2px;
            padding: 4px;
            background-color: var(--score-color);
            color: #fff;
            font-size: 12px;
            border-bottom-left-radius: 12px;
            z-index: 111;
          }
          .table-info {
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
            padding: 15px;
            margin-bottom: 0px;
            box-shadow: 4px 0 8px -2px rgba(0,0,0,0.1),   /* 右侧阴影 */
              -4px 0 8px -2px rgba(0,0,0,0.1),  /* 左侧阴影 */
              0 4px 8px -2px rgba(0,0,0,0.1);   /* 底部阴影 */
            position: relative;
            .candidate-name {
              padding: 10px;
              display: flex;
              align-items: center;
              .img-avatar {
                width: 60px;
                height: 60px;
                border-radius: 100%;
                cursor: pointer;
                img {
                  width: 60px;
                  height: 60px;
                  border-radius: 100%;
                }
              }
              .name-location {
                margin-left: 10px;
                flex: 1;
                max-width: 65%;
                .name {
                  font-size: 18px;
                  font-weight: 700;
                  margin-bottom: 4px;
                  white-space: nowrap;
                  word-break: break-word;
                  cursor: pointer;
                  &:hover {
                    color: #0096cc;
                  }
                }
                .location {
                  .iconfont {
                    font-size: 12px;
                  }
                  .location-name {
                    font-size: 12px;
                    white-space: nowrap;
                    word-break: break-word;
                    display: inline-block;
                    vertical-align: middle;
                    max-width: 77%;
                  }
                }
              }
            }
            .candidate-info-content {
              .item {
                display: flex;
                align-items: center;
                .comparison-result {
                  padding-left: 10px;
                  flex: 1;
                  word-break: break-word;
                  white-space: normal;
                  color: #909399;
                }
                .result-icon {
                  width: 6px;
                  height: 6px;
                  background-color: var(--criterion-score-color);
                  border-radius: 100%;
                  margin: 0 10px;
                }
              }
              .is-max {
                background-color: #e6f2ee;
              }
            }
            .summary-info {
              overflow: auto;

              .summary-body {
                padding: 20px 10px 10px 10px;
                background-color: #f4f4f4;
                position: relative;
                border-radius: 4px;
                .content {
                  .title {
                    font-size: 14px;
                    font-weight: 700;
                    margin-bottom: 12px;
                    text-align: center;
                  }
                  .ai-recommend {
                    position: absolute;
                    right: 0px;
                    top: 0px;
                    padding: 4px;
                    background-color: var(--score-color);
                    color: #fff;
                    font-size: 12px;
                    border-bottom-left-radius: 12px;
                  }
                  .summary {
                    margin-top: 10px;
                    color: #909399;
                    word-break: break-word;
                    white-space: pre-wrap;
                    margin-bottom: 12px;
                  }
                }
              }
            }
            .no-comparison-tip {
              width: 100%;
              height: 246px;
              position: absolute;
              top: 145px;
              padding: 15px;
              margin: auto;
              left: 0;
              background-color: #fafafa;
              display: flex;
              justify-content: center;
              align-items: center;
              word-break: break-all;
              white-space: normal;
              color: #909399;
            }
            &:hover {
              z-index: 1;
              border-top: 0;
              border-left: 1px solid var(--score-color);
              border-bottom: 1px solid var(--score-color);
              border-right: 1px solid var(--score-color);
              box-shadow:
                4px 0 8px -2px rgba(0,0,0,0.1),
                -4px 0 8px -2px rgba(0,0,0,0.1),
                0 4px 8px -2px rgba(0,0,0,0.1);
            }
          }
          &:hover {
            box-shadow: 0 16px 38px -12px rgba(0, 0, 0, 0.56), 0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
          }
        }
      }
      .is-last {
        .block-candidate {
          .table-info {
            box-shadow: -2px 0 2px rgba(239, 239, 239), 0 2px 2px rgb(239, 239, 239), 2px 0 2px rgb(239, 239, 239);
          }
        }
      }
    }
  }
  .ai-comparison-loading {
    width: 100%;
    height: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    .start-label {
      white-space: normal;
      word-break: break-word;
      font-size: 14px;
    }
    .ai-loading-container {
      margin: auto auto 20px auto;
      padding: 0 120px;

      .icon {
        margin: 51px 0 51px 0;
        width: auto;
        height: 40px;
      }

      .text {
        display: flex;
        align-items: center;
        .label {
          color: #b0b3b7;
          font-size: 12px;
          margin-left: 8px;
          flex: 1;
          white-space: normal;
          word-break: break-word;
        }
        margin-bottom: 12px;
      }
    }
  }
  ::v-deep {
    .el-dialog {
      width: var(--dialog-width);
    }
  }
}
/* 整个滚动条 */
::-webkit-scrollbar {
  height: 14px; /* 滚动条高度 */
  width: 14px; /* 滚动条宽度 */
}
</style>
